<script lang="ts">
  import { onDestroy, setContext } from 'svelte';
  import {
    CLOSE_DROPDOWN_CONTEXT_NAME,
    UPDATE_DROPDOWN_STYLE_CONTEXT_NAME
  } from "./constant";

  export let show: boolean = false;
  export let position: "top-bottom" | "left-right" = "top-bottom";
  export let alignment: "left"| "right" = "left";
  export let distanceToDropdown: number = 4; // the distance from the trigger to the dropdown (px)
  export let distanceToEdge: number = 8; // the distance from the dropdown to the edge of the screen (px)
  export let closeOnClickOutside: boolean = true;
  export let closeOnEscapeKey: boolean = true;
  export let width: number = -1;
  export let isMoreButtonDropdown: boolean = false;

  setContext(CLOSE_DROPDOWN_CONTEXT_NAME, notifyCloseDropdown);
  setContext(UPDATE_DROPDOWN_STYLE_CONTEXT_NAME, forceUpdateDropdownStyle);

  let wrapperElement: HTMLElement;
  let triggerElement: HTMLElement;
  let dropdownElement: HTMLElement;
  let dropdownIconElement: HTMLElement;
  
  let dropdownTop: number;
  let dropdownLeft: number;
  let dropdownHeight: number;
  let dropdownMaxHeight: number;

  function addListeners() {
    window.addEventListener("resize", handleWindowResize);
    window.addEventListener("mousedown", handleMousedownOutside);
    window.addEventListener("keydown", handleCloseDropdownOnEscapeKey);
	}

	function removeListeners() {
		window.removeEventListener("resize", handleWindowResize);
    window.removeEventListener("mousedown", handleMousedownOutside);
    window.removeEventListener("keydown", handleCloseDropdownOnEscapeKey);
	}

  function notifyCloseDropdown() {
    show = false;
  }

  function handleWindowResize() {
    updateDropdownPosition();
  }

  function handleMousedownOutside(event: Event) {
    if (closeOnClickOutside && wrapperElement && !wrapperElement.contains(event.target as HTMLElement)) {
      notifyCloseDropdown();
    }
  }

  function handleCloseDropdownOnEscapeKey(event: KeyboardEvent) {
    if (closeOnEscapeKey && event.key === "Escape") {
      notifyCloseDropdown();
    }
  }

  function handleDropdownClose() {
    removeListeners();
  }

  function handleDropdownOpen(shapeChanged: boolean = false) {
    updateDropdownPosition(shapeChanged);
    addListeners();
  }

  function forceUpdateDropdownStyle() {
    const dropdownStyle: any = {
      top: "unset",
      left: "unset",
      height: "unset",
      width: "unset",
    }
    Object.assign(dropdownElement.style, dropdownStyle);

    updateDropdownPosition(true);
  }

  let initDropdownHeight: number = 0;

  function updateDropdownPosition(shapeChanged: boolean = false) {
    const triggerBound = triggerElement.getBoundingClientRect();
    const dropdownBound = dropdownElement.getBoundingClientRect();
    const dropdownIconBound = dropdownIconElement.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    
    if (shapeChanged) {
      initDropdownHeight = dropdownBound.height;
    }

    if (position === "top-bottom") {
      const distanceToTop = triggerBound.top;
      const distanceToBottom = windowHeight - triggerBound.bottom;
      const dropdownPostion: "top" | "bottom"
          = (distanceToBottom >= distanceToTop) ? "bottom" : "top";

      const dropdownWidth = width > 0 ? width : dropdownBound.width;
      if (alignment === "left") {
        dropdownLeft = Math.round(
          Math.min(
            triggerBound.left,
            Math.max(windowWidth - dropdownWidth - distanceToEdge, 0)
          )
        );
      } else {
        dropdownLeft = Math.round(
          Math.max(
            Math.min(triggerBound.right, windowWidth) - dropdownWidth,
            distanceToEdge
          )
        )
      }

      if (dropdownPostion === "bottom") {
        dropdownTop = Math.round(triggerBound.bottom + distanceToDropdown);
        dropdownMaxHeight = Math.round(windowHeight - dropdownTop - distanceToEdge);
        dropdownHeight = Math.round(
          Math.min(dropdownMaxHeight, initDropdownHeight)
        );
      } else {
        dropdownMaxHeight = Math.round(triggerBound.top - distanceToDropdown - distanceToEdge);
        dropdownHeight = Math.round(
          Math.min(dropdownMaxHeight, initDropdownHeight)
        );
        dropdownTop = Math.round(triggerBound.top - distanceToDropdown - dropdownHeight);
      }
    } else {
      // prefer right over left, can be overlapped
      const distanceToLeft = triggerBound.left;
      const distanceToRight = windowWidth - triggerBound.right;
      const dropdownPosition: "right" | "left"
          = (distanceToRight >= distanceToLeft) || (distanceToRight > dropdownBound.width + distanceToDropdown + distanceToEdge)
            ? "right" : "left";

      dropdownMaxHeight = windowHeight - distanceToEdge;
      dropdownHeight = Math.round(
        Math.min(dropdownMaxHeight, initDropdownHeight)
      );
      dropdownTop = dropdownHeight > (windowHeight - triggerBound.top - distanceToEdge)
                    ? (windowHeight - dropdownHeight - distanceToEdge) : triggerBound.top;

      if (dropdownPosition === "right") {
        dropdownLeft = Math.round(
          Math.min(
            triggerBound.right + distanceToDropdown,
            Math.max(windowWidth - distanceToDropdown - dropdownBound.width - distanceToEdge, 0)
          )
        );
      } else {
        dropdownLeft = Math.round(
          Math.min(
            Math.max(triggerBound.left - distanceToDropdown - dropdownBound.width, 0),
            Math.max(windowWidth - distanceToDropdown - dropdownBound.width - distanceToEdge, 0)
          )
        );
      }
    }

    const dropdownIconStyle = {
      top: (triggerBound.bottom + 6) + "px",
      left: Math.round((triggerBound.left + triggerBound.right) / 2 - dropdownIconBound.width / 2 ) + "px",
    };

    const dropdownStyle: any = {
      // top: dropdownTop + "px",
      // left: dropdownLeft + "px",
      top: dropdownTop + document.body.scrollTop + window.scrollY + "px",
      left: dropdownLeft + document.body.scrollLeft + window.scrollX + "px",
      height: dropdownHeight + "px",
    }

    if (width > 0) {
      dropdownStyle.width = Math.min(width, windowWidth - 2 * distanceToEdge) + "px";
    }

    Object.assign(dropdownElement.style, dropdownStyle);
    Object.assign(dropdownIconElement.style, dropdownIconStyle);
  }

  $:if (dropdownElement) {
      handleDropdownOpen(true);
    } else {
      handleDropdownClose();
    }

  onDestroy(() => {
    removeListeners();
  })

  const dropdownIconImage = `
    <svg width="22" height="17" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.39782 1.14436C7.19769 0.0738107 8.80231 0.0738117 9.60218 1.14437L15.1376 8.55292C16.123 9.87184 15.1818 11.75 13.5354 11.75H2.46464C0.818222 11.75 -0.122997 9.87184 0.862452 8.55291L6.39782 1.14436Z" fill="white"/>
    </svg>
  `;
</script>

<div bind:this={wrapperElement}>
  <div class="flex items-center" bind:this={triggerElement}>
    <slot name="button"/>
  </div>

  {#if show && $$slots.content}
    <div
      bind:this={dropdownElement}
      class="dropdown-container fixed inline-block bg-transparent {isMoreButtonDropdown ? "z-[2060]" : "z-[2070]"}"
      style={width > 0 ? `width: ${width}px;` : ""}
    >
      <slot name="content"/>

      <div
        bind:this={dropdownIconElement}
        class="fixed z-10"
      >
        {#if isMoreButtonDropdown}
          {@html dropdownIconImage}
        {/if}
      </div>
    </div>
  {/if}
</div>
